<template>
  <div class="short-summary small-padding-xs medium-padding-s">
    <header @mousedown.stop @mouseup.stop @click.stop>
      <ProductTitle white class="interactive" />
      <ReviewRating white class="short-summary-reviews interactive" scroll-behavior="smooth" />
    </header>
    <footer class="short-summary-footer">
      <div v-if="!!productDescription" class="product-details" data-test-id="product-details" v-html="productDescription"></div>
      <button class="view-more link-body interactive" @mousedown.stop @mouseup.stop @click.stop.prevent="showProductDetailsDrawer">
        View full details
      </button>
    </footer>
  </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, markRaw, computed } from 'vue';
import sanitize from '@nuxt-common/util/app/utils/sanitize';

import ProductTitle from '~/components/productTitle/ProductTitle.common.vue';
import ReviewRating from '~/modules/reviews/components/ReviewRating.vue';

import { useBrand } from '~/composables/useBrand';
import { useSlideout } from '~/composables/utils/useSlideout';

import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductDetails, useProductDetailsProvide } from '~/modules/productDetails/composables/useProductDetails';

useProductDetailsProvide();

const { isMcom } = useBrand();
const { productId, productData: product } = useProduct();
const { productDescription: rawProductDescription, productSummaryBullets } = useProductDetails();
const { showSlideout } = useSlideout();

const productDescription = computed(() => {
  if (rawProductDescription.value) {
    return rawProductDescription.value;
  }

  if (productSummaryBullets.value?.length) {
    const listItems = productSummaryBullets.value.map((bullet: { type: string; text: string }) => `<li>${sanitize(bullet.text)}</li>`).join('');
    return `<ul class="details-list">${listItems}</ul>`;
  }

  return '';
});

const showProductDetailsDrawer = () => {
  showSlideout({
    id: 'product-details-drawer',
    title: isMcom ? 'Product details' : 'Product Details',
    component: markRaw(defineAsyncComponent(() => import('~/modules/productDetails/components/slideouts/ProductDetailsDrawer.vue'))),
    backable: false,
    props: {
      productId,
      product,
    },
  });
};
</script>






<style lang="scss" scoped>
@import '~settingSCSS';

.short-summary {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 16px;
  color: $white;
  height: 100%;

  .interactive {
    pointer-events: auto;
  }

  :deep(.product-title) {
    .brand {
      pointer-events: auto;
    }
  }

  @include breakpoint(medium up) {
    gap: 24px;
  }

  .product-details {
    @include truncate-multiline(2);
    color: $white;

    @include breakpoint(medium up) {
      color: $white;
      @include truncate-multiline(3);
    }

    :deep(.details-list) {
      list-style-type: disc;
      padding-left: rem-calc(16);
      margin: 0;

      li {
        display: list-item;

        a {
          color: $white;
          text-decoration: none;
        }
      }
    }
  }

  .short-summary-footer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    position: relative;
  }

  .view-more {
    position: initial;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
    }
  }
}


@import '~settingSCSS';

.short-summary {
  .product-details,
  .view-more {
    @include body(regular);
    color: $white;

    @include breakpoint(medium up) {
      @include body(medium);
      color: $white;
    }
  }

  :deep(.product-title) {
    .brand {
      pointer-events: auto;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 25px;
      letter-spacing: -0.2px;
      margin-bottom: 4px !important;

      @include breakpoint(medium up) {
        font-size: 22px;
        line-height: 28px;
      }
    }

    .title {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
    }
  }

  :deep(.short-summary-reviews) {
    .rating-block,
    .rating-description .small {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
    }
  }
}
</style>